import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  FilterIcon,
  X,
  Settings,
  GripVertical,
  RotateCcw,
  InfoIcon,
  DollarSign,
} from "lucide-react";
// import { Button } from "@/components/ui/button";
import Button from "@components/ButtonComponent";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Transaction } from "@/components/Transaction";
import GradientText from "@/components/GradiantTitle";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import { ReconciliationTable } from "@/components/ReconciliationTable/ReconciliationTable";
import data from "./dummy.json";
import { getMixedFontClass } from "@/utils/font-utils";
import BadgeComponent from "@components/BadgeComponent";

import {
  DashboardData,
  DataNode,
  Filter,
  FilterState,
  Topic,
  TenantApiResponse,
  TenantsData,
  TenantOrg,
  Tenant,
  Area,
  Outlet,
  FiltersApiResponse,
  FilterApiItem,
  TopicApi,
  DashboardApiResponse,
  DashboardNode,
} from "@/types";
import { apiHelper } from "@/helpers/apiHelper";
import { CashPositionModal } from "@/components/CashPositionModal";
import {
  DashboardHeader,
  SelectionControls,
  TopicsHierarchyModal,
  FilterSection,
  DashboardTableSection,
  CashPositionSection,
  ActionButtonsSection,
} from "./SubComponents";

const ReconciliationDashboard = () => {
  // State variables

  const [dashboardData, setDashboardData] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [availableFilters, setAvailableFilters] = useState([]);
  const [filterState, setFilterState] = useState({});
  const [appliedFilterState, setAppliedFilterState] = useState({});
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [cumulativeLoading, setCumulativeLoading] = useState(false);
  const [filtersData, setFiltersData] = useState({ Filters: [] });
  const [topicsData, setTopicsData] = useState({ Topics: [] });
  const [tenantsData, setTenantsData] = useState({ Tenants: [] });
  const [topicsHierarchy, setTopicsHierarchy] = useState({});
  const [isHierarchyModalOpen, setIsHierarchyModalOpen] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [tempTopicsHierarchy, setTempTopicsHierarchy] = useState({});
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [isCashPositionModalOpen, setIsCashPositionModalOpen] = useState(false);
  const [selectedCashPositionOutlet, setSelectedCashPositionOutlet] =
    useState(null);

  // New selection states
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedOutlets, setSelectedOutlets] = useState([]);
  const [explicitlyDeselectedOutlets, setExplicitlyDeselectedOutlets] =
    useState([]); // Track user deselections
  const [selectedTenant, setSelectedTenant] = useState("");
  const [selectedBusinessDay, setSelectedBusinessDay] = useState(() => {
    // Temporary test date: March 31, 2025
    // return "2025-03-31";
    const today = new Date().toISOString().split("T")[0];
    return today;
  });
  const [debouncedBusinessDay, setDebouncedBusinessDay] = useState(() => {
    // Temporary test date: March 31, 2025
    // return "2025-03-31";
    const today = new Date().toISOString().split("T")[0];
    return today;
  });
  const [tempBusinessDay, setTempBusinessDay] = useState(null); // Temporary date for date picker
  const [cumulativeFromDate, setCumulativeFromDate] = useState(null);
  const [debouncedCumulativeDate, setDebouncedCumulativeDate] = useState(null);
  const [tempCumulativeFromDate, setTempCumulativeFromDate] = useState(null); // Temporary date for cumulative date picker

  // Ref to track if cumulative date change is due to business day change
  const isAutoUpdatingCumulative = useRef(false);

  // Ref to track if user has manually set the cumulative date
  const isManualCumulativeDate = useRef(true); // Set to true for testing with manual date

  // Ref to track request generation to cancel stale second API calls
  const requestGenerationRef = useRef(0);
  const pendingSecondApiCallsRef = useRef(new Map()); // Map of callId -> generation
  const currentActiveSecondCallGenerationRef = useRef(null); // Track which generation's second call is currently active
  const abortControllersRef = useRef(new Map()); // Map of callId -> AbortController to cancel requests

  // Refs to track previous values for comparison
  const prevBusinessDayRef = useRef(null);
  const prevCumulativeDateRef = useRef(null);
  const prevAreasRef = useRef([]);
  const prevOutletsRef = useRef([]);
  const prevTopicsRef = useRef([]);
  const prevFiltersRef = useRef({});
  const prevHierarchyRef = useRef({});

  // Ref to track when outlets are being synced from area changes (to prevent double data loads)
  const isSyncingOutletsRef = useRef(false);

  // Initialize topics hierarchy from localStorage or defaults
  const getDefaultHierarchyForTopic = useCallback(
    (topicTag) => {
      const topic = topicsData.Topics.find((t) => t.Tag === topicTag);
      return topic?.DefaultFilterHierarchy || [];
    },
    [topicsData.Topics]
  );

  const initializeTopicsHierarchy = useCallback(() => {
    const savedHierarchies = localStorage.getItem("topicsHierarchy_v2_");
    if (savedHierarchies) {
      try {
        const parsed = JSON.parse(savedHierarchies);
        setTopicsHierarchy(parsed);
      } catch (error) {
        setTopicsHierarchy({});
      }
    } else {
      setTopicsHierarchy({});
    }
  }, []);

  // Save topics hierarchy to localStorage
  const saveTopicsHierarchy = useCallback((hierarchies) => {
    localStorage.setItem("topicsHierarchy_v2_", JSON.stringify(hierarchies));
    setTopicsHierarchy(hierarchies);
  }, []);

  // Drag and drop handlers for hierarchy items
  const handleDragStart = (e, topicTag, index) => {
    setDraggedItem({ topicTag, index });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", JSON.stringify({ topicTag, index }));
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, topicTag, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (
      draggedItem &&
      draggedItem.topicTag === topicTag &&
      draggedItem.index !== index
    ) {
      setDragOverIndex({ topicTag, index });
    }
  };

  const handleDragLeave = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, targetTopicTag, targetIndex) => {
    e.preventDefault();

    if (!draggedItem) return;

    const { topicTag: sourceTopicTag, index: sourceIndex } = draggedItem;

    if (sourceTopicTag !== targetTopicTag) {
      handleDragEnd();
      return;
    }

    if (sourceIndex === targetIndex) {
      handleDragEnd();
      return;
    }

    const newHierarchies = { ...tempTopicsHierarchy };
    const sourceHierarchy = [...newHierarchies[sourceTopicTag]];

    const [movedItem] = sourceHierarchy.splice(sourceIndex, 1);
    sourceHierarchy.splice(targetIndex, 0, movedItem);
    newHierarchies[sourceTopicTag] = sourceHierarchy;

    setTempTopicsHierarchy(newHierarchies);
    handleDragEnd();
  };

  // Arrow up/down handlers for hierarchy items
  const moveFilterUp = (topicTag, index) => {
    if (index === 0) return; // Already at the top

    const newHierarchies = { ...tempTopicsHierarchy };
    const hierarchy = [...newHierarchies[topicTag]];

    // Swap with previous item
    [hierarchy[index - 1], hierarchy[index]] = [
      hierarchy[index],
      hierarchy[index - 1],
    ];
    newHierarchies[topicTag] = hierarchy;

    setTempTopicsHierarchy(newHierarchies);
  };

  const moveFilterDown = (topicTag, index) => {
    const hierarchy = tempTopicsHierarchy[topicTag] || [];
    if (index === hierarchy.length - 1) return; // Already at the bottom

    const newHierarchies = { ...tempTopicsHierarchy };
    const newHierarchy = [...newHierarchies[topicTag]];

    // Swap with next item
    [newHierarchy[index], newHierarchy[index + 1]] = [
      newHierarchy[index + 1],
      newHierarchy[index],
    ];
    newHierarchies[topicTag] = newHierarchy;

    setTempTopicsHierarchy(newHierarchies);
  };

  // Get filter label for display
  const getFilterLabel = (filterTag) => {
    const filter = filtersData.Filters.find((f) => f.Tag === filterTag);
    return filter?.Label || filterTag;
  };

  // Helper functions to get areas and outlets from selected tenant
  const getSelectedTenant = useCallback(() => {
    return tenantsData.Tenants.find(
      (tenant) => tenant.TenantId.toString() === selectedTenant
    );
  }, [selectedTenant, tenantsData.Tenants]);

  const getAvailableAreas = useCallback(() => {
    const tenant = getSelectedTenant();
    return tenant?.Areas || [];
  }, [getSelectedTenant]);

  const getAvailableOutlets = useCallback(() => {
    const areas = getAvailableAreas();

    // Always show all outlets from all areas regardless of area selection
    const allOutlets = areas.flatMap((area) => area.Outlets);
    const uniqueOutlets = allOutlets.filter(
      (outlet, index, self) =>
        index === self.findIndex((o) => o.OutletId === outlet.OutletId)
    );
    return uniqueOutlets;
  }, [getAvailableAreas]);

  const getTenantCode = useCallback(() => {
    const tenant = getSelectedTenant();
    return tenant?.TenantCode || "";
  }, [getSelectedTenant]);

  // Check if hierarchy has changed from default
  const hasHierarchyChanged = (topicTag, useTempState = false) => {
    const currentHierarchy = useTempState
      ? tempTopicsHierarchy[topicTag] || topicsHierarchy[topicTag] || []
      : topicsHierarchy[topicTag] || [];
    const defaultHierarchy = getDefaultHierarchyForTopic(topicTag);

    if (currentHierarchy.length !== defaultHierarchy.length) return true;
    return currentHierarchy.some(
      (item, index) => item !== defaultHierarchy[index]
    );
  };

  // Reset hierarchy to default for a specific topic
  const resetTopicHierarchy = (topicTag) => {
    const newHierarchies = { ...tempTopicsHierarchy };
    newHierarchies[topicTag] = [...getDefaultHierarchyForTopic(topicTag)];
    setTempTopicsHierarchy(newHierarchies);
  };

  // Handle opening the hierarchy modal
  const handleOpenHierarchyModal = () => {
    setTempTopicsHierarchy({ ...topicsHierarchy });
    setIsHierarchyModalOpen(true);
  };

  // Handle closing the hierarchy modal and applying changes
  const handleCloseHierarchyModal = () => {
    saveTopicsHierarchy(tempTopicsHierarchy);
    setIsHierarchyModalOpen(false);
  };

  // Handle canceling changes
  const handleCancelHierarchyModal = () => {
    setTempTopicsHierarchy({});
    setIsHierarchyModalOpen(false);
  };

  // Get available filter tags for a topic
  const getAvailableFilterTagsForTopic = (topicTag) => {
    const topic = topicsData.Topics.find((t) => t.Tag === topicTag);
    if (!topic) return [];

    const currentHierarchy = tempTopicsHierarchy[topicTag] || [];

    return topic.AvailableFilterTags.filter(
      (tag) => !currentHierarchy.includes(tag) && tag !== "CUMULATIVE_FROM_DATE"
    );
  };

  // Check if a filter is a default filter for a topic
  const isDefaultFilter = (topicTag, filterTag) => {
    const defaultTags = getDefaultHierarchyForTopic(topicTag);
    return defaultTags.includes(filterTag);
  };

  // Remove a filter tag from hierarchy
  const removeFilterFromHierarchy = (topicTag, filterTag) => {
    const newHierarchies = { ...tempTopicsHierarchy };
    if (newHierarchies[topicTag]) {
      const currentHierarchy = newHierarchies[topicTag];

      // Check if we're trying to remove AREA or OUTLET
      if (filterTag === "AREA" || filterTag === "OUTLET") {
        // Check if both AREA and OUTLET are present
        const hasArea = currentHierarchy.includes("AREA");
        const hasOutlet = currentHierarchy.includes("OUTLET");

        // If both are present, allow removal of either
        // If only one is present, don't allow removal (Topic + one of Area/Outlet required)
        if (hasArea && hasOutlet) {
          newHierarchies[topicTag] = currentHierarchy.filter(
            (tag) => tag !== filterTag
          );
        } else {
          // Don't allow removal if only one of AREA/OUTLET is present
          return;
        }
      } else {
        // For other filters, allow removal
        newHierarchies[topicTag] = currentHierarchy.filter(
          (tag) => tag !== filterTag
        );
      }
    }
    setTempTopicsHierarchy(newHierarchies);
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getPreviousDay = (dateString) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() - 1);
    return date.toISOString().split("T")[0];
  };

  const getNextDay = (dateString) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + 1);
    return date.toISOString().split("T")[0];
  };

  // Helper function to get cumulative from date (7 days prior)
  const getCumulativeFromDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  };

  // Helper function to find a node by ID in the entire tree
  const findNodeById = useCallback((nodes, targetId) => {
    if (!nodes || nodes.length === 0) return null;

    for (const node of nodes) {
      if (node.Id === targetId) {
        return node;
      }
      if (node.ChildNodes && node.ChildNodes.length > 0) {
        const found = findNodeById(node.ChildNodes, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Helper function to find a node by NodeTag in the entire tree (fallback when ID doesn't match)
  const findNodeByTag = useCallback((nodes, targetTag) => {
    if (!nodes || nodes.length === 0) return null;

    for (const node of nodes) {
      if (node.NodeTag === targetTag) {
        return node;
      }
      if (node.ChildNodes && node.ChildNodes.length > 0) {
        const found = findNodeByTag(node.ChildNodes, targetTag);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Function to merge only cumulative fields from new data into existing data
  const mergeCumulativeFields = useCallback(
    (existingNode, newDataTree) => {
      if (!existingNode) return existingNode;

      // Strategy 1: Try matching by ID first (most specific)
      let matchingNewNode = findNodeById(newDataTree, existingNode.Id);
      let matchMethod = "ID";

      // Strategy 2: If not found by ID, try matching by NodeTag (more stable identifier)
      if (!matchingNewNode && existingNode.NodeTag) {
        matchingNewNode = findNodeByTag(newDataTree, existingNode.NodeTag);
        if (matchingNewNode) {
          matchMethod = "NodeTag";
        }
      }

      if (!matchingNewNode) {
        // Node not found in new data tree
        // Check if existing cumulative values are 0 or undefined - if so, explicitly set to 0
        // This ensures consistency when a node has no cumulative activity
        const hasExistingCumulativeData =
          existingNode.RecordsVerification?.CumulativeVariances?.Exceptions >
            0 ||
          existingNode.RecordsVerification?.CumulativeVariances?.Outstanding >
            0 ||
          existingNode.SettlementVerification?.CumulativeVariances?.Exceptions >
            0 ||
          existingNode.SettlementVerification?.CumulativeVariances
            ?.AwaitingSettlement > 0;

        if (hasExistingCumulativeData) {
        }

        // Set cumulative values to 0 for consistency (node has no cumulative activity in new data)
        const nodeWithZeroCumulative = {
          ...existingNode,
          RecordsVerification: {
            ...existingNode.RecordsVerification,
            CumulativeVariances: {
              Outstanding: 0,
              Exceptions: 0,
            },
          },
          SettlementVerification: {
            ...existingNode.SettlementVerification,
            CumulativeVariances: {
              AwaitingSettlement: 0,
              Exceptions: 0,
            },
          },
        };

        // Still process children
        if (existingNode.ChildNodes && existingNode.ChildNodes.length > 0) {
          nodeWithZeroCumulative.ChildNodes = existingNode.ChildNodes.map(
            (child) => mergeCumulativeFields(child, newDataTree)
          );
        }

        return nodeWithZeroCumulative;
      }

      // Log successful match with cumulative data

      // Deep merge cumulative fields while preserving all other fields
      const merged = {
        ...existingNode,
        RecordsVerification: {
          ...existingNode.RecordsVerification,
          CumulativeVariances: matchingNewNode.RecordsVerification
            ?.CumulativeVariances
            ? {
                Outstanding:
                  matchingNewNode.RecordsVerification.CumulativeVariances
                    .Outstanding,
                Exceptions:
                  matchingNewNode.RecordsVerification.CumulativeVariances
                    .Exceptions,
              }
            : {
                Outstanding: 0,
                Exceptions: 0,
              },
        },
        SettlementVerification: {
          ...existingNode.SettlementVerification,
          CumulativeVariances: matchingNewNode.SettlementVerification
            ?.CumulativeVariances
            ? {
                AwaitingSettlement:
                  matchingNewNode.SettlementVerification.CumulativeVariances
                    .AwaitingSettlement,
                Exceptions:
                  matchingNewNode.SettlementVerification.CumulativeVariances
                    .Exceptions,
              }
            : {
                AwaitingSettlement: 0,
                Exceptions: 0,
              },
        },
      };

      // Recursively merge child nodes
      if (existingNode.ChildNodes && existingNode.ChildNodes.length > 0) {
        merged.ChildNodes = existingNode.ChildNodes.map((existingChild) =>
          mergeCumulativeFields(existingChild, newDataTree)
        );
      }

      return merged;
    },
    [findNodeById, findNodeByTag]
  );

  // Helper function to update cumulative loading state based on pending calls for current generation
  const updateCumulativeLoadingState = useCallback(() => {
    const currentGeneration = requestGenerationRef.current;

    // Check if there are any pending calls for the current generation
    const hasPendingCallsForCurrentGeneration = Array.from(
      pendingSecondApiCallsRef.current.values()
    ).some((generation) => generation === currentGeneration);

    // Show loading if there are any pending calls for the current generation
    // Don't check currentActiveSecondCallGenerationRef as it can be null/cleared during cleanup
    if (hasPendingCallsForCurrentGeneration) {
      setCumulativeLoading(true);
    } else {
      setCumulativeLoading(false);
    }
  }, []);

  // Transform new dashboard API response to expected format
  const transformDashboardResponse = useCallback((apiResponse) => {
    // Transform nodes structure
    const transformNode = (node) => ({
      Id: node.Id,
      NodeTag: node.NodeTag,
      NodeLabel: node.NodeLabel,
      NodeValue: node.NodeValue,
      RecordsVerification: node.RecordsVerification,
      SettlementVerification: node.SettlementsVerification, // Note: API uses 'SettlementsVerification'
      ChildNodes: node.Nodes ? node.Nodes.map(transformNode) : [],
    });

    // Use first area and outlet from the response
    const area = apiResponse.Areas[0] || {};
    const outlet = apiResponse.Outlets[0] || {};

    return {
      AreaId: area.AreaId || 0,
      AreaCode: area.AreaCode || "",
      AreaName: area.AreaName || "",
      OutletId: outlet.OutletId || 0,
      OutletCode: outlet.OutletCode || "",
      OutletName: outlet.OutletName || "",
      BusinessDay: apiResponse.BusinessDay,
      calculatedCumilativeFromDate: apiResponse.CumulativeFromDate,
      ChildNodes: apiResponse.Nodes.map(transformNode),
    };
  }, []);

  const navigateToDate = (direction) => {
    if (!selectedBusinessDay) return;

    const currentDate = new Date(selectedBusinessDay);
    const newDate = new Date(currentDate);

    if (direction === "prev") {
      newDate.setDate(currentDate.getDate() - 1);
    } else {
      newDate.setDate(currentDate.getDate() + 1);
    }

    const newDateString = newDate.toISOString().split("T")[0];
    setSelectedBusinessDay(newDateString);
    setDebouncedBusinessDay(newDateString);
  };

  const viewTransaction = (row) => {
    setHighlightedRowId(row.Id);
    setTimeout(() => {
      setHighlightedRowId(null);
      setSelectedTransaction(row);
    }, 500);
  };

  const closeTransaction = () => {
    if (selectedTransaction) {
      setHighlightedRowId(selectedTransaction.Id);
      setTimeout(() => {
        setHighlightedRowId(null);
      }, 1000);
    }
    setSelectedTransaction(null);
  };

  // Get selected outlet details for cash position modal
  const getSelectedOutletDetails = () => {
    const outletDetails = [];

    tenantsData.Tenants.forEach((tenant) => {
      tenant.Areas.forEach((area) => {
        area.Outlets.forEach((outlet) => {
          if (selectedOutlets.includes(outlet.OutletId.toString())) {
            outletDetails.push({
              id: outlet.OutletId.toString(),
              name: outlet.OutletName,
              code: outlet.OutletCode,
            });
          }
        });
      });
    });

    return outletDetails;
  };

  const handleCashPositionClick = (outletId, outletName) => {
    setSelectedCashPositionOutlet({ id: outletId, name: outletName });
    setIsCashPositionModalOpen(false);
  };

  // Filter handling functions
  const handleFilterChange = (filterTag, values) => {
    if (values.length === 0) {
      // Remove filter from state
      setFilterState((prev) => ({
        ...Object.fromEntries(
          Object.entries(prev).filter(([key]) => key !== filterTag)
        ),
      }));

      // DON'T remove filter from hierarchy when deselecting all values
      // This prevents the issue where removing all selected values also removes the filter from hierarchy
      // The filter should remain in the hierarchy even if no values are selected
    } else {
      setFilterState((prev) => ({
        ...prev,
        [filterTag]: values,
      }));
    }
  };

  const removeFilter = (filterTag) => {
    setFilterState((prev) => ({
      ...Object.fromEntries(
        Object.entries(prev).filter(([key]) => key !== filterTag)
      ),
    }));

    // DON'T remove filter from hierarchy when manually removing filter
    // This prevents the issue where removing all selected values also removes the filter from hierarchy
    // The filter should remain in the hierarchy even if no values are selected
  };

  // Check if there are unapplied filter changes
  const hasUnappliedChanges = () => {
    const currentKeys = Object.keys(filterState);
    const appliedKeys = Object.keys(appliedFilterState);

    if (currentKeys.length !== appliedKeys.length) return true;

    for (const key of currentKeys) {
      const currentValues = filterState[key] || [];
      const appliedValues = appliedFilterState[key] || [];

      if (currentValues.length !== appliedValues.length) return true;

      const currentSorted = [...currentValues].sort();
      const appliedSorted = [...appliedValues].sort();

      for (let i = 0; i < currentSorted.length; i++) {
        if (currentSorted[i] !== appliedSorted[i]) return true;
      }
    }

    return false;
  };

  const handleApplyFilters = () => {
    setAppliedFilterState(filterState);
  };

  const resolveAppliesToTopic = (filterTag) => {
    const appliesToTopics = topicsData.Topics.filter((topic) =>
      topic.AvailableFilterTags.includes(filterTag)
    );
    return (
      <div
        key={filterTag}
        className="text-xs text-primary opacity-100 font-medium"
      >
        <Tooltip className="opacity-100">
          <TooltipTrigger>
            <InfoIcon className="w-4 h-4" />
          </TooltipTrigger>
          <TooltipContent className="text-xs !bg-white !opacity-100 p-3 py-2 italic font-semibold ">
            This filter applies to
            <span className="font-medium px-1 grid grid-cols-2 gap-1 mt-1">
              {appliesToTopics
                ?.map((topic) => topic.Label)
                .map((label) => (
                  <span key={label}>{label}</span>
                ))}
            </span>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  // Dynamic filter names from API response
  const areaLiteral =
    filtersData.Filters.find((f) => f.Tag === "AREA")?.Label || "DC";
  const outletLiteral =
    filtersData.Filters.find((f) => f.Tag === "OUTLET")?.Label || "Cashier";
  const tenantLiteral = "Tenant";

  // API functions (commented out for now - will be replaced with project's API pattern)
  const updateAvailableFilters = useCallback(() => {
    // Make all filters available globally (not topic-specific)
    // also ignore AREA and OUTLET filters
    const allFilters = filtersData.Filters.filter(
      (filter) =>
        filter.Values &&
        filter.Values.length > 0 &&
        filter.Tag !== "AREA" &&
        filter.Tag !== "OUTLET"
    );

    setAvailableFilters(allFilters);

    // No need to clear filter state since all filters are always available
  }, [filtersData]);

  // Transform new filters API response to expected format
  const transformFiltersResponse = useCallback((apiResponse) => {
    // Transform filters
    const transformedFilters = apiResponse.Filters.filter(
      (filter) => filter.Values !== null
    ) // Only include filters with values
      .map((filter) => ({
        Tag: filter.ColumnTag,
        Label: filter.ColumnName,
        Values: filter.Values.map((value) => ({
          Code: value.Value,
          Label: value.Label,
        })),
      }));

    // Transform topics (structure is already compatible)
    const transformedTopics = apiResponse.Topics;

    return {
      Filters: transformedFilters,
      Topics: transformedTopics,
    };
  }, []);

  const fetchFilters = useCallback(async () => {
    try {
      setFiltersLoading(true);

      const response = await apiHelper({
        externalurl: "/api/v1/Common/GetFilters",
        activityTag: "GetFilters",
        onlyBody: {
          TenantCode: null,
          Context: "ReconciliationDashboardFilters",
        },
        method: "POST",
        headerContentType: "application/json",
      });

      if (response && response.Topics && response.Filters) {
        const transformedData = transformFiltersResponse(response);
        setFiltersData({ Filters: transformedData.Filters });
        setTopicsData({ Topics: transformedData.Topics });
      } else {
        setFiltersData({ Filters: [] });
        setTopicsData({ Topics: [] });
      }
    } catch (error) {
      setFiltersData({ Filters: [] });
      setTopicsData({ Topics: [] });
    } finally {
      setFiltersLoading(false);
    }
  }, [transformFiltersResponse]);

  // Transform flat tenant org array into nested hierarchy structure
  const transformTenantHierarchy = useCallback((tenantOrgs) => {
    const tenants = [];
    const orgMap = new Map();

    // First pass: create a map of all orgs by their ID
    tenantOrgs.forEach((org) => {
      orgMap.set(org.OrgId, { ...org, children: [] });
    });

    // Second pass: build the hierarchy
    tenantOrgs.forEach((org) => {
      if (org.ParentOrgId !== null) {
        const parent = orgMap.get(org.ParentOrgId);
        if (parent) {
          parent.children.push(orgMap.get(org.OrgId));
        }
      }
    });

    // Third pass: transform to the expected structure
    tenantOrgs.forEach((org) => {
      if (org.Level === 0) {
        // Tenant level
        const orgDetail =
          org.OrgDetails.find((detail) => detail.LanguageCode === "en") ||
          org.OrgDetails[0];
        const orgCodeAttr = org.OrgAttributes.find(
          (attr) => attr.AttributeTypeTag === "ORG_EXTERNAL_CODE"
        );

        const tenant = {
          TenantId: org.OrgId,
          TenantCode: orgCodeAttr
            ? orgCodeAttr.AttributeValue
            : org.OrgExternalReference,
          TenantName: orgDetail ? orgDetail.LongName : org.OrgExternalReference,
          Areas: [],
        };

        // Find areas (Level 1) that belong to this tenant
        const areas = tenantOrgs.filter(
          (area) => area.Level === 1 && area.ParentOrgId === org.OrgId
        );

        areas.forEach((areaOrg) => {
          const areaDetail =
            areaOrg.OrgDetails.find((detail) => detail.LanguageCode === "en") ||
            areaOrg.OrgDetails[0];
          const areaCodeAttr = areaOrg.OrgAttributes.find(
            (attr) => attr.AttributeTypeTag === "ORG_EXTERNAL_CODE"
          );

          const area = {
            AreaId: areaOrg.OrgId,
            AreaCode: areaCodeAttr
              ? areaCodeAttr.AttributeValue
              : areaOrg.OrgExternalReference,
            AreaName: areaDetail
              ? areaDetail.LongName
              : areaOrg.OrgExternalReference,
            Outlets: [],
          };

          // Find outlets (Level 2) that belong to this area
          const outlets = tenantOrgs.filter(
            (outlet) =>
              outlet.Level === 2 && outlet.ParentOrgId === areaOrg.OrgId
          );

          outlets.forEach((outletOrg) => {
            const outletDetail =
              outletOrg.OrgDetails.find(
                (detail) => detail.LanguageCode === "en"
              ) || outletOrg.OrgDetails[0];
            const outletCodeAttr = outletOrg.OrgAttributes.find(
              (attr) => attr.AttributeTypeTag === "ORG_EXTERNAL_CODE"
            );

            area.Outlets.push({
              OutletId: outletOrg.OrgId,
              OutletCode: outletCodeAttr
                ? outletCodeAttr.AttributeValue
                : outletOrg.OrgExternalReference,
              OutletName: outletDetail
                ? outletDetail.LongName
                : outletOrg.OrgExternalReference,
            });
          });

          tenant.Areas.push(area);
        });

        tenants.push(tenant);
      }
    });

    return { Tenants: tenants };
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      setTenantsLoading(true);

      const response = await apiHelper({
        externalurl: "/api/v1/Membership/GetTenantHierarchy",
        activityTag: "GetTenantHierarchy",
        onlyBody: {
          TenantCode: null,
        },
        method: "POST",
        headerContentType: "application/json",
      });

      if (response && response.TenantOrgs) {
        const transformedData = transformTenantHierarchy(response.TenantOrgs);
        setTenantsData(transformedData);
      } else {
        console.error("Invalid response structure:", response);
        setTenantsData({ Tenants: [] });
      }
    } catch (error) {
      setTenantsData({ Tenants: [] });
    } finally {
      setTenantsLoading(false);
    }
  }, [transformTenantHierarchy]);

  const loadDashboardData = useCallback(
    async (currentState) => {
      try {
        // Use passed state or get current values from closure
        const {
          topics = selectedTopics,
          hierarchy = topicsHierarchy,
          areas = selectedAreas,
          outlets = selectedOutlets,
          filters = appliedFilterState,
          businessDay = debouncedBusinessDay,
          cumulativeDate = debouncedCumulativeDate,
          isCumulativeOnlyUpdate = false, // New flag to indicate cumulative-only update
        } = currentState || {};

        // Only show full loading if this is NOT a cumulative-only update
        if (!isCumulativeOnlyUpdate) {
          setLoading(true); // Show table loader immediately when business day changes
        }
        // Don't set cumulativeLoading here - it will be set when the second API call starts

        const currentTopicsData = topics.map((topicTag) => {
          const topicHierarchy = hierarchy[topicTag] || [];
          const dashboardHierarchy = topicHierarchy.join("|");

          // Build filters for this topic - now global (not topic-specific)
          const topicFilters = [];

          // Add area filters - always include if areas are selected
          if (areas.length > 0) {
            topicFilters.push({
              Tag: "AREA",
              Values: areas,
            });
          }

          // Add outlet filters - always include if outlets are selected
          if (outlets.length > 0) {
            topicFilters.push({
              Tag: "OUTLET",
              Values: outlets,
            });
          }

          // Add applied filters - include all filters globally
          Object.keys(filters).forEach((filterTag) => {
            const values = filters[filterTag] || [];
            if (values.length > 0) {
              topicFilters.push({
                Tag: filterTag,
                Values: values,
              });
            }
          });

          const topicData = {
            Tag: topicTag,
            DashboardHierarchy: dashboardHierarchy,
          };

          // Only include Filters if there are actually filters to send
          if (topicFilters.length > 0) {
            topicData.Filters = topicFilters;
          }

          return topicData;
        });

        // Check if we need two API calls or just one
        // If cumulativeDate is null, we only need one call
        // const needsTwoAPICalls = cumulativeDate !== null;
        const needsTwoAPICalls = true;

        if (needsTwoAPICalls) {
          // If this is a cumulative-only update, skip the first call
          // and keep existing data visible
          if (!isCumulativeOnlyUpdate) {
            // Cancel all pending second API calls when starting a new first API call
            requestGenerationRef.current += 1;
            const currentGeneration = requestGenerationRef.current;

            // Abort all pending second API calls before clearing
            abortControllersRef.current.forEach((controller, callId) => {
              controller.abort();
            });

            // Clear all pending second API calls and abort controllers
            pendingSecondApiCallsRef.current.clear();
            abortControllersRef.current.clear();
            currentActiveSecondCallGenerationRef.current = null; // Clear active second call generation
            setCumulativeLoading(false);

            // Reset loading state - new generation starts fresh
            updateCumulativeLoadingState();

            // First call - same day cumulative (faster response)
            const firstCallPayload = {
              BusinessDay: businessDay,
              CumulativeFromDate: null, // Same as business day for faster response
              Topics: currentTopicsData,
              IsCumulativeFetching: false,
            };

            const firstResponse = await apiHelper({
              externalurl:
                "/api/v1/Reconciliation/GetReconciliationConsoleData",
              activityTag: "GetReconciliationConsoleData",
              onlyBody: firstCallPayload,
              method: "POST",
              headerContentType: "application/json",
            });

            if (firstResponse && firstResponse.Nodes) {
              // Check if this response is still relevant (generation hasn't changed)
              if (requestGenerationRef.current === currentGeneration) {
                const transformedData =
                  transformDashboardResponse(firstResponse);
                setDashboardData(transformedData);
                //   setDashboardData(null);
                setLoading(false); // Clear loading after first API call completes
              } else {
              }
            }
          } else {
          }

          // Second call - user-selected cumulative date (slower response)
          const secondCallGeneration = requestGenerationRef.current;
          const secondCallPayload = {
            BusinessDay: businessDay,
            CumulativeFromDate: cumulativeDate, // User-selected cumulative date
            Topics: currentTopicsData,
            IsCumulativeFetching: true,
          };

          // Track this second API call with its generation
          const secondCallId = Date.now();
          pendingSecondApiCallsRef.current.set(
            secondCallId,
            secondCallGeneration
          );

          // Check if running in Tauri - if so, don't use AbortController
          // Tauri's fetch implementation handles abort signals differently
          const isTauri = window.__TAURI_INTERNALS__ !== undefined;
          let abortController = null;

          if (!isTauri) {
            // Only use AbortController in browser environment
            abortController = new AbortController();
            abortControllersRef.current.set(secondCallId, abortController);
          }

          // Set this as the current active second call generation
          currentActiveSecondCallGenerationRef.current = secondCallGeneration;

          // Update cumulative loading state now that we have a pending call
          // This will show the loader immediately
          updateCumulativeLoadingState();

          try {
            const secondResponse = await apiHelper({
              externalurl:
                "/api/v1/Reconciliation/GetReconciliationConsoleData",
              activityTag: "GetReconciliationConsoleData",
              onlyBody: secondCallPayload,
              method: "POST",
              headerContentType: "application/json",
              abortSignal: abortController?.signal || null, // Only pass signal if not in Tauri
            });

            // Remove from pending calls and abort controllers
            pendingSecondApiCallsRef.current.delete(secondCallId);
            if (abortController) {
              abortControllersRef.current.delete(secondCallId);
            }

            // CRITICAL: Check generation BEFORE any state updates
            // Only process if this response is still relevant and generation hasn't changed
            // Add more lenient check for Tauri
            const generationStillValid =
              requestGenerationRef.current === secondCallGeneration;

            if (!generationStillValid) {
              // Clear active generation if this was it
              if (
                currentActiveSecondCallGenerationRef.current ===
                secondCallGeneration
              ) {
                currentActiveSecondCallGenerationRef.current = null;
                // Update loading state: check if any pending calls are for the current generation
                updateCumulativeLoadingState();
              }
              return; // Early return - don't process this stale response
            }

            // Only process if response has valid data
            if (secondResponse && secondResponse.Nodes) {
              const newTransformedData =
                transformDashboardResponse(secondResponse);

              // Merge only cumulative fields to avoid flicker

              // Check generation hasn't changed before state update
              // Only check requestGenerationRef - activeGeneration can be null if cleared by another process
              if (requestGenerationRef.current === secondCallGeneration) {
                setDashboardData((prevData) => {
                  // Double-check generation hasn't changed during state update
                  if (requestGenerationRef.current !== secondCallGeneration) {
                    return prevData;
                  }

                  if (!prevData) {
                    return newTransformedData;
                  }

                  // Log data structures for debugging
                  const prevTerminals =
                    prevData?.ChildNodes?.[4]?.ChildNodes?.[0]?.ChildNodes?.[0]
                      ?.ChildNodes;
                  const newTerminals =
                    newTransformedData?.ChildNodes?.[4]?.ChildNodes?.[0]
                      ?.ChildNodes?.[0]?.ChildNodes;

                  // Pass the entire new data tree to mergeCumulativeFields
                  // so it can find matching nodes at any level
                  const mergedData = {
                    ...prevData,
                    calculatedCumilativeFromDate:
                      newTransformedData.calculatedCumilativeFromDate,
                    ChildNodes: prevData.ChildNodes.map((existingNode) => {
                      const merged = mergeCumulativeFields(
                        existingNode,
                        newTransformedData.ChildNodes
                      );
                      return merged;
                    }),
                  };
                  return mergedData;
                });

                // Clear active second call generation and loading state only if this is the current generation
                if (
                  currentActiveSecondCallGenerationRef.current ===
                  secondCallGeneration
                ) {
                  currentActiveSecondCallGenerationRef.current = null;
                }
                // Update loading state based on remaining pending calls
                updateCumulativeLoadingState();
              } else {
                // Clear active generation if this was it
                if (
                  currentActiveSecondCallGenerationRef.current ===
                  secondCallGeneration
                ) {
                  currentActiveSecondCallGenerationRef.current = null;
                }
                // Update loading state based on remaining pending calls
                updateCumulativeLoadingState();
              }
            } else {
            }
          } catch (error) {
            // Remove from pending calls and abort controllers on error
            pendingSecondApiCallsRef.current.delete(secondCallId);
            if (abortController) {
              abortControllersRef.current.delete(secondCallId);
            }

            // Don't log aborted requests as errors
            if (error.name === "AbortError") {
              // Clear active generation if this was it
              if (
                currentActiveSecondCallGenerationRef.current ===
                secondCallGeneration
              ) {
                currentActiveSecondCallGenerationRef.current = null;
              }
              // Update loading state based on remaining pending calls
              updateCumulativeLoadingState();

              // Loading is already cleared after first API call, no need to handle here
              return; // Early return for aborted requests
            }

            // Clear active generation if this was it
            if (
              currentActiveSecondCallGenerationRef.current ===
              secondCallGeneration
            ) {
              currentActiveSecondCallGenerationRef.current = null;
            }
            // Update loading state based on remaining pending calls
            updateCumulativeLoadingState();

            // Loading is already cleared after first API call, no need to handle here
          }
        } else {
          // Single call - cumulative date is null
          const singleCallPayload = {
            BusinessDay: businessDay,
            CumulativeFromDate: null, // Always null for single call
            Topics: currentTopicsData,
          };

          const response = await apiHelper({
            externalurl: "/api/v1/Reconciliation/GetReconciliationConsoleData",
            activityTag: "GetReconciliationConsoleData",
            onlyBody: singleCallPayload,
            method: "POST",
            headerContentType: "application/json",
          });

          if (response && response.Nodes) {
            const transformedData = transformDashboardResponse(response);
            setDashboardData(transformedData);
          }
        }
      } catch (error) {
        setDashboardData(null);
        // Clear loading on error if not aborted
        if (error.name !== "AbortError") {
          setLoading(false);
        }
      } finally {
        // Don't clear loading here - it's managed when second API call completes
        // Only clear cumulativeLoading if not already managed
        // Don't clear cumulativeLoading here - it's managed by the second API call
      }
    },
    [
      transformDashboardResponse,
      mergeCumulativeFields,
      updateCumulativeLoadingState,
    ]
  ); // Include transform, merge, and update functions

  // Load saved state from localStorage on component mount
  useEffect(() => {
    const savedState = localStorage.getItem("dashboardStatev3");
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        if (parsedState.selectedAreas)
          setSelectedAreas(parsedState.selectedAreas);
        if (parsedState.selectedOutlets)
          setSelectedOutlets(parsedState.selectedOutlets);
        if (parsedState.explicitlyDeselectedOutlets)
          setExplicitlyDeselectedOutlets(
            parsedState.explicitlyDeselectedOutlets
          );
        if (parsedState.selectedArea && !parsedState.selectedAreas)
          setSelectedAreas([parsedState.selectedArea]);
        if (parsedState.selectedOutlet && !parsedState.selectedOutlets)
          setSelectedOutlets([parsedState.selectedOutlet]);
        if (parsedState.selectedTenant)
          setSelectedTenant(parsedState.selectedTenant);
        if (parsedState.selectedBusinessDay) {
          setSelectedBusinessDay(parsedState.selectedBusinessDay);
          setDebouncedBusinessDay(parsedState.selectedBusinessDay);
        }
        if (parsedState.cumulativeFromDate) {
          setCumulativeFromDate(parsedState.cumulativeFromDate);
          setDebouncedCumulativeDate(parsedState.cumulativeFromDate);
          // If loading from localStorage, consider it as manually set to preserve user's choice
          isManualCumulativeDate.current = true;
        }
        if (parsedState.selectedTopics)
          setSelectedTopics(parsedState.selectedTopics);
        if (parsedState.filterState) {
          setFilterState(parsedState.filterState);
          setAppliedFilterState(parsedState.filterState);
        }
      } catch (error) {
        console.error("Error loading saved dashboard state:", error);
      }
    }
    initializeTopicsHierarchy();
    setIsInitialLoadComplete(true);
  }, [initializeTopicsHierarchy]);

  // Populate default hierarchies when topics data is loaded
  useEffect(() => {
    if (topicsData.Topics.length > 0) {
      const currentHierarchies = { ...topicsHierarchy };
      let hasChanges = false;

      topicsData.Topics.forEach((topic) => {
        if (
          !currentHierarchies[topic.Tag] ||
          currentHierarchies[topic.Tag].length === 0
        ) {
          currentHierarchies[topic.Tag] = [...topic.DefaultFilterHierarchy];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setTopicsHierarchy(currentHierarchies);
        saveTopicsHierarchy(currentHierarchies);
      }
    }
  }, [topicsData.Topics, topicsHierarchy, saveTopicsHierarchy]);

  // Auto-select all items on initial load
  useEffect(() => {
    // Auto-select all tenants if none selected
    if (tenantsData.Tenants.length > 0 && !selectedTenant) {
      setSelectedTenant(tenantsData.Tenants[0].TenantId.toString());
    }
  }, [tenantsData.Tenants, selectedTenant]);

  // Auto-select all areas and outlets when tenant is selected
  // Only auto-select if BOTH areas and outlets are empty (Topic + one of Area/Outlet required)
  useEffect(() => {
    if (tenantsData.Tenants.length > 0 && selectedTenant) {
      const availableAreas = getAvailableAreas();
      const availableOutlets = getAvailableOutlets();

      // Only auto-select if BOTH areas and outlets are empty
      // This ensures Topic + one of Area/Outlet requirement is maintained
      const bothEmpty =
        selectedAreas.length === 0 && selectedOutlets.length === 0;

      if (bothEmpty) {
        // Auto-select all areas if none selected
        if (availableAreas.length > 0) {
          const areaIds = availableAreas.map((area) => area.AreaId.toString());
          setSelectedAreas(areaIds);
        }

        // Auto-select all outlets if none selected
        if (availableOutlets.length > 0) {
          const outletIds = availableOutlets.map((outlet) =>
            outlet.OutletId.toString()
          );
          setSelectedOutlets(outletIds);
        }
      } else {
        console.log(
          "Skipping auto-selection - at least one of Area/Outlet has values"
        );
      }
    }
  }, [
    tenantsData.Tenants,
    selectedTenant,
    selectedAreas,
    selectedOutlets,
    getAvailableAreas,
    getAvailableOutlets,
  ]);

  // Auto-select all topics when topics data is loaded
  useEffect(() => {
    if (topicsData.Topics.length > 0 && selectedTopics.length === 0) {
      setSelectedTopics(topicsData.Topics.map((topic) => topic.Tag));
    }
  }, [topicsData.Topics, selectedTopics]);

  // Add filter to hierarchy (no auto-selection)
  const addFilterToHierarchy = (topicTag, filterTag) => {
    const newHierarchies = { ...tempTopicsHierarchy };
    if (!newHierarchies[topicTag]) {
      newHierarchies[topicTag] = [];
    }
    newHierarchies[topicTag] = [...newHierarchies[topicTag], filterTag];
    setTempTopicsHierarchy(newHierarchies);
  };

  // Validate saved selections after tenant data is loaded
  useEffect(() => {
    if (tenantsData.Tenants.length > 0 && selectedTenant) {
      const availableAreas = getAvailableAreas();
      const validAreas = selectedAreas.filter((areaId) =>
        availableAreas.some((area) => area.AreaId.toString() === areaId)
      );

      // Only update if there are invalid areas
      if (validAreas.length !== selectedAreas.length) {
        setSelectedAreas(validAreas);
        // Don't automatically clear outlets - let user decide
      }

      // Validate outlets only if areas are valid and outlets exist
      if (validAreas.length > 0) {
        const availableOutlets = getAvailableOutlets();
        const validOutlets = selectedOutlets.filter((outletId) =>
          availableOutlets.some(
            (outlet) => outlet.OutletId.toString() === outletId
          )
        );
        // Only update if there are invalid outlets
        if (validOutlets.length !== selectedOutlets.length) {
          setSelectedOutlets(validOutlets);
        }
      }
    }
  }, [
    tenantsData.Tenants,
    selectedTenant,
    selectedAreas,
    selectedOutlets,
    getAvailableAreas,
    getAvailableOutlets,
  ]);

  // Clear explicitly deselected outlets when their parent area is deselected
  // This ensures outlets come back when area is re-selected
  useEffect(() => {
    if (
      tenantsData.Tenants.length > 0 &&
      selectedTenant &&
      explicitlyDeselectedOutlets.length > 0
    ) {
      const availableAreas = getAvailableAreas();

      // Get all outlets from currently selected areas
      const outletsFromSelectedAreas = availableAreas
        .filter((area) => selectedAreas.includes(area.AreaId.toString()))
        .flatMap((area) => area.Outlets || [])
        .map((outlet) => outlet.OutletId.toString());

      // Remove outlets from explicitly deselected list if their area is no longer selected
      const outletsToClear = explicitlyDeselectedOutlets.filter(
        (outletId) => !outletsFromSelectedAreas.includes(outletId)
      );

      if (outletsToClear.length > 0) {
        setExplicitlyDeselectedOutlets((prev) =>
          prev.filter((id) => !outletsToClear.includes(id))
        );
      }
    }
  }, [
    selectedAreas,
    tenantsData.Tenants,
    selectedTenant,
    getAvailableAreas,
    explicitlyDeselectedOutlets,
  ]);

  // Sync outlets with selected areas - add outlets when area is re-selected
  // useEffect(() => {
  //   if (
  //     tenantsData.Tenants.length > 0 &&
  //     selectedTenant &&
  //     selectedAreas.length > 0
  //   ) {
  //     const availableAreas = getAvailableAreas();

  //     // Get all outlets from currently selected areas
  //     const outletsFromSelectedAreas = availableAreas
  //       .filter((area) => selectedAreas.includes(area.AreaId.toString()))
  //       .flatMap((area) => area.Outlets || [])
  //       .map((outlet) => outlet.OutletId.toString());

  //     // Remove duplicates
  //     const uniqueOutletsFromAreas = [...new Set(outletsFromSelectedAreas)];

  //     // Get outlets that should be added (in selected areas but not in selectedOutlets)
  //     // Exclude outlets that were explicitly deselected by the user
  //     const outletsToAdd = uniqueOutletsFromAreas.filter(
  //       (outletId) =>
  //         !selectedOutlets.includes(outletId) &&
  //         !explicitlyDeselectedOutlets.includes(outletId)
  //     );

  //     // Only add outlets, don't remove them (validation effect handles removal)
  //     if (outletsToAdd.length > 0) {
  //       const updatedOutlets = [...selectedOutlets, ...outletsToAdd];

  //       // Mark that we're syncing outlets so data loading effect knows to batch the changes
  //       isSyncingOutletsRef.current = true;
  //       setSelectedOutlets(updatedOutlets);
  //       // Reset the flag after state update (will be applied in next render)
  //       // Use setTimeout to ensure it runs after the state update is processed
  //       setTimeout(() => {
  //         isSyncingOutletsRef.current = false;
  //       }, 0);
  //     }
  //   }
  // }, [
  //   selectedAreas,
  //   tenantsData.Tenants,
  //   selectedTenant,
  //   getAvailableAreas,
  //   selectedOutlets,
  //   explicitlyDeselectedOutlets,
  // ]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const stateToSave = {
      selectedAreas,
      selectedOutlets,
      explicitlyDeselectedOutlets,
      selectedTenant,
      selectedBusinessDay,
      cumulativeFromDate,
      selectedTopics,
      filterState,
    };
    localStorage.setItem("dashboardStatev3", JSON.stringify(stateToSave));
  }, [
    selectedAreas,
    selectedOutlets,
    explicitlyDeselectedOutlets,
    selectedTenant,
    selectedBusinessDay,
    cumulativeFromDate,
    selectedTopics,
    filterState,
  ]);

  useEffect(() => {
    updateAvailableFilters();
  }, [updateAvailableFilters]);

  // Fetch filters and tenants only once after initial load is complete
  useEffect(() => {
    if (isInitialLoadComplete) {
      fetchFilters();
      fetchTenants();
    }
  }, [isInitialLoadComplete, fetchFilters, fetchTenants]);

  // Debounce business day changes with 2-second delay that resets on each change
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBusinessDay(selectedBusinessDay);
    }, 2000);

    return () => clearTimeout(timer);
  }, [selectedBusinessDay]);

  // Debounce cumulative date changes with 2-second delay that resets on each change
  useEffect(() => {
    // Skip debouncing if this is an automatic update due to business day change
    if (isAutoUpdatingCumulative.current) {
      isAutoUpdatingCumulative.current = false; // Reset flag
      setDebouncedCumulativeDate(cumulativeFromDate);
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedCumulativeDate(cumulativeFromDate);
    }, 2000);

    return () => clearTimeout(timer);
  }, [cumulativeFromDate]);

  // Load dashboard data when all required conditions are met
  useEffect(() => {
    // Require at least one of Area or Outlet (Topic + one of Area/Outlet required)
    const hasAreaOrOutlet =
      selectedAreas.length > 0 || selectedOutlets.length > 0;

    const shouldLoadData =
      hasAreaOrOutlet &&
      selectedTopics.length > 0 &&
      debouncedBusinessDay &&
      isInitialLoadComplete &&
      !filtersLoading &&
      !tenantsLoading;

    if (shouldLoadData) {
      // Check if this is the first load
      const isFirstLoad = prevBusinessDayRef.current === null;

      // Check if only cumulative date has changed
      const businessDayChanged =
        !isFirstLoad && prevBusinessDayRef.current !== debouncedBusinessDay;
      const cumulativeDateChanged =
        !isFirstLoad &&
        prevCumulativeDateRef.current !== debouncedCumulativeDate;

      // Check if areas/outlets changed (compare arrays without mutating)
      const sortedPrevAreas = [...prevAreasRef.current].sort();
      const sortedCurrentAreas = [...selectedAreas].sort();
      const areasChanged =
        !isFirstLoad &&
        JSON.stringify(sortedPrevAreas) !== JSON.stringify(sortedCurrentAreas);

      const sortedPrevOutlets = [...prevOutletsRef.current].sort();
      const sortedCurrentOutlets = [...selectedOutlets].sort();
      const outletsChangedRaw =
        !isFirstLoad &&
        JSON.stringify(sortedPrevOutlets) !==
          JSON.stringify(sortedCurrentOutlets);

      // If outlets changed but we're currently syncing them from an area change,
      // don't treat it as a separate change (to prevent double data loads)
      const outletsChanged = outletsChangedRaw && !isSyncingOutletsRef.current;

      // Check if topics changed (compare arrays without mutating)
      const sortedPrevTopics = [...prevTopicsRef.current].sort();
      const sortedCurrentTopics = [...selectedTopics].sort();
      const topicsChanged =
        !isFirstLoad &&
        JSON.stringify(sortedPrevTopics) !==
          JSON.stringify(sortedCurrentTopics);

      // Check if filters changed (compare objects)
      const filtersChanged =
        !isFirstLoad &&
        JSON.stringify(prevFiltersRef.current) !==
          JSON.stringify(appliedFilterState);

      // Check if hierarchy changed (compare objects)
      const hierarchyChanged =
        !isFirstLoad &&
        JSON.stringify(prevHierarchyRef.current) !==
          JSON.stringify(topicsHierarchy);

      // Determine if this is a cumulative-only update
      // Only true if this is NOT the first load and ONLY cumulative date changed
      const isCumulativeOnlyUpdate =
        !isFirstLoad &&
        cumulativeDateChanged &&
        !businessDayChanged &&
        !areasChanged &&
        !outletsChanged &&
        !topicsChanged &&
        !filtersChanged &&
        !hierarchyChanged;

      // Update refs for next comparison
      prevBusinessDayRef.current = debouncedBusinessDay;
      prevCumulativeDateRef.current = debouncedCumulativeDate;
      prevAreasRef.current = [...selectedAreas];
      prevOutletsRef.current = [...selectedOutlets];
      prevTopicsRef.current = [...selectedTopics];
      prevFiltersRef.current = { ...appliedFilterState };
      prevHierarchyRef.current = { ...topicsHierarchy };

      loadDashboardData({
        topics: selectedTopics,
        hierarchy: topicsHierarchy,
        areas: selectedAreas,
        outlets: selectedOutlets,
        filters: appliedFilterState,
        businessDay: debouncedBusinessDay,
        cumulativeDate: debouncedCumulativeDate,
        isCumulativeOnlyUpdate: isCumulativeOnlyUpdate,
      });
    }
  }, [
    selectedAreas,
    selectedOutlets,
    selectedTopics,
    debouncedBusinessDay,
    debouncedCumulativeDate,
    appliedFilterState,
    topicsHierarchy,
    isInitialLoadComplete,
    filtersLoading,
    tenantsLoading,
    loadDashboardData,
  ]);

  // Handle escape key to close cash position modals
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        if (selectedCashPositionOutlet) {
          setSelectedCashPositionOutlet(null);
        } else if (isCashPositionModalOpen) {
          setIsCashPositionModalOpen(false);
        }
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [selectedCashPositionOutlet, isCashPositionModalOpen]);

  return (
    <div className="max-w-8xl mx-auto px-6 sm:px-8 lg:px-10 py-4">
      {/* Enhanced Dashboard Title Section */}
      <div className="mb-2 animate-fade-in">
        {/* Breadcrumb Navigation */}

        {/* Selection Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex flex-col">
            <DashboardHeader />
            <SelectionControls
              tenantsLoading={tenantsLoading}
              tenantsData={tenantsData}
              selectedTenant={selectedTenant}
              setSelectedTenant={setSelectedTenant}
              tenantLiteral={tenantLiteral}
              areaLiteral={areaLiteral}
              outletLiteral={outletLiteral}
              selectedAreas={selectedAreas}
              setSelectedAreas={setSelectedAreas}
              selectedOutlets={selectedOutlets}
              setSelectedOutlets={setSelectedOutlets}
              explicitlyDeselectedOutlets={explicitlyDeselectedOutlets}
              setExplicitlyDeselectedOutlets={setExplicitlyDeselectedOutlets}
              filtersLoading={filtersLoading}
              topicsData={topicsData}
              selectedTopics={selectedTopics}
              setSelectedTopics={setSelectedTopics}
              getAvailableAreas={getAvailableAreas}
              getAvailableOutlets={getAvailableOutlets}
            />
          </div>
          {/* Action Buttons */}
          <ActionButtonsSection
            filtersLoading={filtersLoading}
            selectedOutlets={selectedOutlets}
            CashPositionSectionComponent={() => (
              <CashPositionSection
                selectedOutlets={selectedOutlets}
                getSelectedOutletDetails={getSelectedOutletDetails}
                handleCashPositionClick={handleCashPositionClick}
                isCashPositionModalOpen={isCashPositionModalOpen}
                setIsCashPositionModalOpen={setIsCashPositionModalOpen}
              />
            )}
            TopicsHierarchyModalComponent={() => (
              <TopicsHierarchyModal
                isOpen={isHierarchyModalOpen}
                onOpenChange={setIsHierarchyModalOpen}
                selectedTopics={selectedTopics}
                topicsData={topicsData}
                tempTopicsHierarchy={tempTopicsHierarchy}
                topicsHierarchy={topicsHierarchy}
                draggedItem={draggedItem}
                dragOverIndex={dragOverIndex}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                moveFilterUp={moveFilterUp}
                moveFilterDown={moveFilterDown}
                getFilterLabel={getFilterLabel}
                hasHierarchyChanged={hasHierarchyChanged}
                resetTopicHierarchy={resetTopicHierarchy}
                getAvailableFilterTagsForTopic={getAvailableFilterTagsForTopic}
                isDefaultFilter={isDefaultFilter}
                removeFilterFromHierarchy={removeFilterFromHierarchy}
                addFilterToHierarchy={addFilterToHierarchy}
                filtersData={filtersData}
                handleOpenModal={handleOpenHierarchyModal}
                handleCloseModal={handleCloseHierarchyModal}
                handleCancelModal={handleCancelHierarchyModal}
              />
            )}
          />
        </div>
      </div>
      {(selectedAreas.length > 0 || selectedOutlets.length > 0) &&
        selectedBusinessDay &&
        <FilterSection
          filtersLoading={filtersLoading}
          filterState={filterState}
          cumulativeFromDate={cumulativeFromDate}
          tempCumulativeFromDate={tempCumulativeFromDate}
          selectedBusinessDay={selectedBusinessDay}
          setCumulativeFromDate={setCumulativeFromDate}
          setDebouncedCumulativeDate={setDebouncedCumulativeDate}
          setTempCumulativeFromDate={setTempCumulativeFromDate}
          availableFilters={availableFilters}
          filtersData={filtersData}
          topicsData={topicsData}
          handleFilterChange={handleFilterChange}
          removeFilter={removeFilter}
          setFilterState={setFilterState}
          hasUnappliedChanges={hasUnappliedChanges}
          handleApplyFilters={handleApplyFilters}
          loading={loading}
        />
      }
      {/* Main Dashboard Table */}
      <DashboardTableSection
        loading={loading}
        dashboardData={dashboardData}
        cumulativeLoading={cumulativeLoading}
        cumulativeFromDate={debouncedCumulativeDate}
        businessDay={debouncedBusinessDay}
        highlightedRowId={highlightedRowId}
        viewTransaction={viewTransaction}
        selectedBusinessDay={selectedBusinessDay}
        tempBusinessDay={tempBusinessDay}
        setTempBusinessDay={setTempBusinessDay}
        setSelectedBusinessDay={setSelectedBusinessDay}
        setDebouncedBusinessDay={setDebouncedBusinessDay}
        formatDate={formatDate}
        getPreviousDay={getPreviousDay}
        getNextDay={getNextDay}
        navigateToDate={navigateToDate}
      />
      {/* Transaction Popup */}
      {selectedTransaction && (
        <Transaction
          row={selectedTransaction}
          onClose={closeTransaction}
          selectedTenant={selectedTenant}
          selectedAreas={selectedAreas}
          selectedOutlets={selectedOutlets}
          selectedBusinessDay={selectedBusinessDay}
          dashboardData={dashboardData?.ChildNodes || []}
          tenantsData={tenantsData}
          topicsData={topicsData}
          filterState={filterState}
          filtersData={filtersData}
        />
      )}
      {selectedCashPositionOutlet && (
        <CashPositionModal
          isOpen={!!selectedCashPositionOutlet}
          onClose={() => setSelectedCashPositionOutlet(null)}
          outletName={selectedCashPositionOutlet.name}
        />
      )}
    </div>
  );
};

export default ReconciliationDashboard;
